import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ArrowLeft, User, Mail, Lock, Save, Loader2, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { PaymentMethodSection } from '../components/PaymentMethodSection';
import BiometricToggle from '../components/BiometricToggle';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AccountSettings = () => {
  const navigate = useNavigate();
  const { user, setUser } = useApp();
  
  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Loading and status states
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Check if user logged in with social account
  const isSocialLogin = user?.authProvider === 'google' || user?.authProvider === 'facebook';

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Pre-fill form with user data (social users still see this page so they
    // can manage their saved payment method, but the profile/password sections
    // are hidden below since those are managed by the social provider).
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setEmail(user.email || '');
  }, [user, navigate]);

  if (!user) return null;

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setIsUpdatingProfile(true);

    try {
      const response = await axios.put(`${API}/user/profile`, {
        email: user.email,
        firstName,
        lastName,
        newEmail: email !== user.email ? email : undefined
      });

      if (response.data.success) {
        setProfileSuccess('Profile updated successfully!');
        // Update local user state
        setUser(prev => ({
          ...prev,
          firstName,
          lastName,
          email: email
        }));
        
        // Clear success message after 3 seconds
        setTimeout(() => setProfileSuccess(''), 3000);
      }
    } catch (error) {
      setProfileError(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      const response = await axios.put(`${API}/user/password`, {
        email: user.email,
        currentPassword,
        newPassword
      });

      if (response.data.success) {
        setPasswordSuccess('Password updated successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Clear success message after 3 seconds
        setTimeout(() => setPasswordSuccess(''), 3000);
      }
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      <Navbar />

      <div className="pt-28 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-600 dark:text-neutral-400 hover:text-gray-900 mb-6 transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black text-gray-900 dark:text-neutral-100">Account Settings</h1>
            <p className="text-gray-600 dark:text-neutral-400 mt-1">Manage your account information</p>
          </div>

          {/* Profile Section (hidden for social-login users — managed by their provider) */}
          {!isSocialLogin && (
          <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-100 dark:border-neutral-800 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gray-100 dark:bg-neutral-800 rounded-full flex items-center justify-center">
                <User size={20} className="text-gray-600 dark:text-neutral-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100">Profile Information</h2>
            </div>

            <form onSubmit={handleUpdateProfile} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-300 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black transition-colors"
                    placeholder="John"
                    data-testid="settings-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-300 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black transition-colors"
                    placeholder="Doe"
                    data-testid="settings-last-name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-300 mb-2">
                  <Mail size={16} className="inline mr-2" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black transition-colors"
                  placeholder="you@example.com"
                  data-testid="settings-email"
                />
              </div>

              {profileError && (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-500/10 rounded-xl">
                  <AlertCircle size={18} />
                  <span>{profileError}</span>
                </div>
              )}

              {profileSuccess && (
                <div className="flex items-center gap-2 text-green-600 dark:text-emerald-400 text-sm p-3 bg-green-50 dark:bg-emerald-500/10 rounded-xl">
                  <CheckCircle size={18} />
                  <span>{profileSuccess}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isUpdatingProfile}
                className="w-full sm:w-auto px-6 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-profile-btn"
              >
                {isUpdatingProfile ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Save size={18} />
                    Save Changes
                  </>
                )}
              </button>
            </form>
          </div>
          )}

          {/* Payment Method Section */}
          <PaymentMethodSection />

          {/* Biometric login toggle — native shell only, renders null on web */}
          <BiometricToggle userEmail={user?.email} />

          {/* Password Section (hidden for social-login users) */}
          {!isSocialLogin && (
          <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-100 dark:border-neutral-800">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gray-100 dark:bg-neutral-800 rounded-full flex items-center justify-center">
                <Lock size={20} className="text-gray-600 dark:text-neutral-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100">Change Password</h2>
            </div>

            <form onSubmit={handleUpdatePassword} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-300 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black transition-colors pr-12"
                    placeholder="••••••••"
                    data-testid="settings-current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500 hover:text-gray-600"
                  >
                    {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-300 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black transition-colors pr-12"
                    placeholder="••••••••"
                    data-testid="settings-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">Minimum 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-300 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black transition-colors"
                  placeholder="••••••••"
                  data-testid="settings-confirm-password"
                />
              </div>

              {passwordError && (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-500/10 rounded-xl">
                  <AlertCircle size={18} />
                  <span>{passwordError}</span>
                </div>
              )}

              {passwordSuccess && (
                <div className="flex items-center gap-2 text-green-600 dark:text-emerald-400 text-sm p-3 bg-green-50 dark:bg-emerald-500/10 rounded-xl">
                  <CheckCircle size={18} />
                  <span>{passwordSuccess}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isUpdatingPassword}
                className="w-full sm:w-auto px-6 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="change-password-btn"
              >
                {isUpdatingPassword ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Lock size={18} />
                    Update Password
                  </>
                )}
              </button>
            </form>
          </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AccountSettings;
